import { useEffect, useState } from 'react';
import ErrorDisplay from '../../../../components/ErrorDisplay';
import { LessonPicker } from '../../../../pickers/lesson/LessonPicker';
import type { LearningLesson } from '../../../Lessons/services/learningTypes';
import type { JourneyItemTemplateLessonFirestore } from '../../services/journeyTypes';
import { JourneyItemFormActions } from './JourneyItemFormActions';
import { JourneyItemFormFields } from './JourneyItemFormFields';
import { useJourneyItemForm } from './useJourneyItemForm';

interface LessonSpecificFormData {
    imageUrl: string;
    lessonColor: string;
    sectionId: string;
    lessonId: string;
}

interface LessonJourneyItemFormProps {
    itemTemplate?: JourneyItemTemplateLessonFirestore;
    initialOrder: number;
    onSave: (item: JourneyItemTemplateLessonFirestore) => void;
    onSaveAndContinue?: (item: JourneyItemTemplateLessonFirestore) => void;
    onCancel: () => void;
}

export const LessonJourneyItemForm = ({ itemTemplate, initialOrder, onSave, onSaveAndContinue, onCancel }: LessonJourneyItemFormProps) => {
    const {
        commonFormData,
        loading,
        error,
        handleCommonChange,
        handleSubmit,
        handleSaveAndContinue,
    } = useJourneyItemForm({
        itemTemplate,
        initialOrder,
        defaultProgressType: 'fixed',
    });

    const [specificFormData, setSpecificFormData] = useState<LessonSpecificFormData>({
        imageUrl: '',
        lessonColor: '#A8E6CF',
        sectionId: '',
        lessonId: '',
    });

    useEffect(() => {
        if (itemTemplate && itemTemplate.id) {
            setSpecificFormData({
                imageUrl: itemTemplate.imageUrl,
                lessonColor: itemTemplate.lessonColor,
                sectionId: itemTemplate.sectionId,
                lessonId: itemTemplate.lessonId,
            });
        }
    }, [itemTemplate]);

    const handleSpecificChange = (field: string, value: string | boolean) => {
        setSpecificFormData((prev) => ({ ...prev, [field]: value }));
    };

    const handleChange = (field: string, value: string | number | boolean) => {
        if (field in commonFormData) {
            handleCommonChange(field as keyof typeof commonFormData, value);
        } else {
            handleSpecificChange(field, value as string | boolean);
        }
    };

    const validateSpecificFields = (): string | null => {
        if (!specificFormData.imageUrl.trim()) {
            return 'Image URL is required for lesson items';
        }
        if (!specificFormData.sectionId.trim()) {
            return 'Section ID is required for lesson items';
        }
        if (!specificFormData.lessonId.trim()) {
            return 'Lesson ID is required for lesson items';
        }
        return null;
    };

    const createItem = (baseData: Partial<JourneyItemTemplateLessonFirestore>): JourneyItemTemplateLessonFirestore => {
        return {
            ...baseData,
            type: 'lesson',
            maxProgress: 1,
            imageUrl: specificFormData.imageUrl,
            lessonColor: specificFormData.lessonColor,
            sectionId: specificFormData.sectionId,
            lessonId: specificFormData.lessonId,
        } as JourneyItemTemplateLessonFirestore;
    };

    const onSubmit = handleSubmit(validateSpecificFields, createItem, onSave);
    const onSaveAndContinueClick = onSaveAndContinue ? handleSaveAndContinue(validateSpecificFields, createItem, onSaveAndContinue) : undefined;

    return (
        <form onSubmit={onSubmit} className="space-y-4">
            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormFields
                formData={commonFormData}
                itemType="lesson"
                disableProgressType={true}
                onChange={handleChange}
            />

            <h2 className="text-lg font-bold mt-2">Lesson-specific fields</h2>
            <hr className="-mt-4" />

            <LessonPicker
                value={specificFormData.lessonId}
                onChange={(sectionId: string, lesson: LearningLesson | null) => {
                    if (lesson) {
                        handleChange('lessonId', lesson?.uid || '');
                        handleChange('imageUrl', lesson.imageSrc);
                        handleChange('lessonColor', lesson.color || '#A8E6CF');
                        handleChange('sectionId', sectionId);
                    } else {
                        handleChange('lessonId', '');
                        handleChange('imageUrl', '');
                        handleChange('lessonColor', '#A8E6CF');
                        handleChange('sectionId', '');
                    }
                }}
                label="Lesson *"
                required
            />

            {specificFormData.imageUrl && (
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Selected Lesson Image</label>
                    <div className="grid border border-gray-300 rounded-md p-4 place-content-center" style={{ backgroundColor: specificFormData.lessonColor }}>
                        <img
                            src={specificFormData.imageUrl}
                            alt={specificFormData.lessonId}
                            className="max-w-full h-auto rounded-md w-32 h-32 object-cover"
                        />
                    </div>
                </div>
            )}

            {error && <ErrorDisplay message={error} />}

            <JourneyItemFormActions
                onCancel={onCancel}
                onSaveAndContinue={onSaveAndContinueClick}
                loading={loading}
                isEditMode={!!itemTemplate?.id}
            />
        </form>
    );
};
